import type { Aggregator } from "../data/aggregator";
import type { TransactionEvent, SpotlightEvent, CorridorStat } from "../data/types";
import { motion } from "../theme/tokens";

export type Mode = "live" | "quiet" | "spotlight" | "zoom" | "replay" | "demo";

type OrchestratorOpts = {
  aggregator: Aggregator;
  onMode: (mode: Mode) => void;
  onSpotlight: (ev: SpotlightEvent | null) => void;
  onRotationSlow: (slow: boolean) => void;
  onZoom?: (countryA: string, countryB: string) => void;
  onZoomReset?: () => void;
};

/**
 * State machine for display behavior.
 *
 * Transitions are signaled out; the rendering layer owns the interpolation.
 * Nothing here ever snaps — it only sets targets.
 */
export class ModeOrchestrator {
  private mode: Mode = "live";
  private lastSpotlightAt = 0;
  private quietSince: number | null = null;
  private spotlightExpiresAt = 0;
  private lastZoomAt = 0;
  private zoomExpiresAt = 0;
  private corridors: CorridorStat[] = [];

  constructor(private readonly opts: OrchestratorOpts) {}

  /** Update the latest corridors data for zoom targeting. */
  setCorridors(corridors: CorridorStat[]) {
    this.corridors = corridors;
  }

  observeEvent(ev: TransactionEvent, now = Date.now()) {
    const threshold = this.opts.aggregator.spotlightThreshold(now);
    const isSpotlight =
      ev.amountUsd >= threshold &&
      now - this.lastSpotlightAt >= motion.spotlightCooldownMs;

    if (isSpotlight) this.triggerSpotlight(ev, now);
  }

  tick(now = performance.now()) {
    // Expire active spotlight banner
    if (this.mode === "spotlight" && now > this.spotlightExpiresAt) {
      this.opts.onSpotlight(null);
      this.opts.onRotationSlow(false);
      this.setMode("live");
    }

    // Expire corridor zoom
    if (this.mode === "zoom" && now > this.zoomExpiresAt) {
      this.opts.onZoomReset?.();
      this.setMode("live");
    }

    // Trigger corridor zoom every ~8 min when in live mode
    if (
      this.mode === "live" &&
      this.corridors.length > 0 &&
      now - this.lastZoomAt > 480_000
    ) {
      this.lastZoomAt = now;
      this.zoomExpiresAt = now + 30_000; // 30s zoom
      this.setMode("zoom");
      const top = this.corridors[0];
      this.opts.onZoom?.(top.countryA, top.countryB);
    }

    // Quiet mode based on rolling TPS
    const tps = this.opts.aggregator.tps();
    if (tps < motion.quietTpsThreshold) {
      this.quietSince ??= now;
      if (now - this.quietSince > motion.quietDwellMs && this.mode === "live") {
        this.setMode("quiet");
        this.opts.onRotationSlow(true);
      }
    } else {
      this.quietSince = null;
      if (this.mode === "quiet") {
        this.setMode("live");
        this.opts.onRotationSlow(false);
      }
    }
  }

  private triggerSpotlight(ev: TransactionEvent, now: number) {
    this.lastSpotlightAt = now;
    this.spotlightExpiresAt = performance.now() + 4000;
    this.opts.onSpotlight({
      id: ev.id,
      amountUsd: ev.amountUsd,
      currencySrc: ev.currencySrc,
      currencyDst: ev.currencyDst,
      originCity: ev.origin.city,
      destCity: ev.dest.city,
      originCountry: ev.origin.country,
      destCountry: ev.dest.country,
      merchant: ev.merchant,
      product: ev.product,
    });
    this.opts.onRotationSlow(true);
    this.setMode("spotlight");
  }

  private setMode(m: Mode) {
    if (this.mode === m) return;
    this.mode = m;
    this.opts.onMode(m);
  }

  get current() {
    return this.mode;
  }
}
